import { useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import { CalendarDaysIcon, MegaphoneIcon } from "@heroicons/react/24/outline";
import StudioClosuresAnnouncementModal, {
  STUDIO_CLOSURES_UPDATE,
} from "../components/announcements/StudioClosuresAnnouncementModal";
import { useStudioPlan } from "../hooks/useStudioPlan";
import { useAuth } from "../contexts/AuthContext";
import { markLatestUpdateViewed } from "../utils/latestUpdate";

type UpdateId = "studio-closure-dates";

export default function Updates() {
  const [openUpdate, setOpenUpdate] = useState<UpdateId | null>(null);
  const { plan, loading: planLoading } = useStudioPlan();
  const { user } = useAuth();

  if (planLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (plan?.accessState !== "paid") {
    return <Navigate to="/app" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Updates | DojoTrack</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {openUpdate === "studio-closure-dates" && (
        <StudioClosuresAnnouncementModal
          enabled
          forceOpen
          onDismiss={() => setOpenUpdate(null)}
        />
      )}

      <div className="relative min-h-full bg-gray-50 dark:bg-gray-900">
        <div className="space-y-6 p-0 pt-10 xl:p-6 xl:pt-0">
          <div>
            <div className="flex items-center gap-2">
              <MegaphoneIcon
                className="h-6 w-6 text-indigo-600 dark:text-indigo-400"
                aria-hidden="true"
              />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Updates
              </h1>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              See what&apos;s new in DojoTrack and revisit important announcements.
            </p>
          </div>

          <div className="max-w-4xl space-y-4">
            <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="border-b border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-5 dark:border-indigo-900 dark:from-indigo-950/60 dark:via-gray-800 dark:to-blue-950/40 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-xl bg-indigo-100 p-3 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                    <CalendarDaysIcon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                        Latest update
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {STUDIO_CLOSURES_UPDATE.publishedLabel}
                      </span>
                    </div>
                    <h2 className="mt-3 text-xl font-bold text-gray-900 dark:text-white">
                      {STUDIO_CLOSURES_UPDATE.title}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {STUDIO_CLOSURES_UPDATE.description}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                <p className="text-sm leading-6 text-gray-600 dark:text-gray-300">
                  See what closure dates do and how to add them in Settings.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setOpenUpdate("studio-closure-dates");
                    markLatestUpdateViewed(user?.id);
                  }}
                  className="flex-none rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  View update
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </>
  );
}
